import { groupLifeProposalLivesKey } from '@/features/proposals/hooks/group-life';
import { AddMemberModal } from '@/features/quotes/components/quote-steps/group-life/add-member.modal';
import { AsyncImportOverlay } from '@/features/quotes/components/quote-steps/group-life/async-import-overlay.component';
import { DuplicateMembersDialog } from '@/features/quotes/components/quote-steps/group-life/duplicate-members.dialog';
import { ImportMembersModal } from '@/features/quotes/components/quote-steps/group-life/import-members.modal';
import { useAsyncMembersImport } from '@/features/quotes/hooks/group-life/use-async-import.hook';
import { resolveMemberDisplayCapital } from '@/features/quotes/utils/group-life/effective-capital';
import { groupLifeService } from '@/services';
import type {
    GroupLifeDuplicateCpfsPayload,
    GroupLifeFrontPlan,
    GroupLifeMemberCrudInput,
    GroupLifeMemberCrudResponse,
} from '@/services/group-life';
import { toaster } from '@/shared/ui/toaster.component';
import { isCpfAlreadyRegistered, mapWithConcurrency } from '@/shared/utils';
import {
    Button,
    DataTable,
    Dialog,
    Text,
} from '@noorden-insurtech/alba-design-system';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Download, Search, Upload, UserPlus } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { PmeLifeRow } from '../../proposal.mock';
import { LifeDetailsModal } from './life-details.modal';
import { createLivesColumns } from './lives-table-columns';

const STIPULATOR_TARGET = '__STIPULATOR__';

/** Requisições simultâneas na reconciliação de vidas importadas. Alto o
 *  suficiente para não serializar centenas de exclusões, baixo o suficiente
 *  para não levar 429/timeout do gateway. */
const RECONCILE_CONCURRENCY = 8;

type MemberData = {
  cpf: string;
  name: string;
  dateOfBirth: string;
  gender: 'M' | 'F';
  insurableGroup: string;
  capital: string;
  awayFromWork: boolean;
  cids: string[];
};

export type LivesExportTarget = { targetId: string; label?: string };

type Props = {
  lives: PmeLifeRow[];
  total: number;
  policyId?: string;
  plan?: GroupLifeFrontPlan;
  /**
   * Múltiplo Salarial: a coluna e o detalhe mostram salário × quantidade
   * de salários. A edição continua recebendo o salário.
   */
  multiplySalary?: boolean;
  showImport?: boolean;
  showIncompleteStatus?: boolean;
  showAddMember?: boolean;
  pageSize?: number;
  /** Opções do seletor. Sem isso, a tabela fica travada no `pageSize` atual. */
  pageSizeOptions?: number[];
  /**
   * Paginação NO SERVIDOR: com `onPageChange`, `lives` já é a página atual e
   * `total` o total de vidas — a tabela não fatia nada. Sem ele, a paginação
   * é local sobre a lista inteira. O segundo argumento é o tamanho escolhido
   * no seletor.
   */
  onPageChange?: (page: number, pageSize?: number) => void;
  /** Carregando a página atual (paginação no servidor). */
  isLoading?: boolean;
  exportTargets?: LivesExportTarget[];
  /**
   * Alteração de vida persistida. `requiresRecalculation` é `false` quando a
   * edição não toca em nada que entre na tarifa (só nome e/ou CPF).
   */
  onLivesChange?: (outcome: { requiresRecalculation: boolean }) => void;
  /**
   * Importação concluída e reconciliada. `requiresRecalculation` reflete o
   * veredito do backend: `UPDATE` (só nome/CPF mudaram) dispensa o recálculo;
   * `COMPLETED` (mudou a quantidade de vidas ou um campo que entra na tarifa)
   * exige.
   */
  onImportCompleted?: (outcome: { requiresRecalculation: boolean }) => void;
  /** Nome obrigatório nas vidas (proposta). */
  requireName?: boolean;
  /**
   * Estipulante dono das vidas desta tabela: `__STIPULATOR__` (master) ou o id
   * do subestipulante. Inclusão, importação e reconciliação usam este target.
   */
  targetId?: string;
  /** Mostra o título "Lista de Vidas" (o agrupador por estipulante já mostra). */
  showTitle?: boolean;
  /**
   * Vidas consideradas na checagem de CPF repetido. Com a lista separada por
   * estipulante, recebe as vidas de todos eles; sem isso, usa `lives`.
   */
  cpfLookupLives?: PmeLifeRow[];
};

export function PmeLivesTableSection({
  lives,
  total,
  policyId,
  plan,
  multiplySalary = false,
  showImport = true,
  showIncompleteStatus = true,
  showAddMember = true,
  pageSize: configuredPageSize,
  pageSizeOptions,
  onPageChange,
  isLoading = false,
  exportTargets,
  onLivesChange,
  onImportCompleted,
  requireName = false,
  targetId = STIPULATOR_TARGET,
  showTitle = true,
  cpfLookupLives,
}: Props) {
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [editLife, setEditLife] = useState<PmeLifeRow | null>(null);
  const [detailsLife, setDetailsLife] = useState<PmeLifeRow | null>(null);
  const [deleteLife, setDeleteLife] = useState<PmeLifeRow | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [duplicateCpfs, setDuplicateCpfs] =
    useState<GroupLifeDuplicateCpfsPayload | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const memberUniqueIdsRef = useRef(new Map<string, string>());
  const completedImportIdRef = useRef<string | null>(null);
  const [asyncImport, setAsyncImport] = useState<{
    importId: string;
    targetId: string;
    totalExpected: number;
    existingMemberIds: string[];
    status?: string;
  } | null>(null);

  const normalizeMemberText = (value?: string) =>
    (value ?? '').trim().toUpperCase();

  const normalizeCpfDigits = (value?: string) =>
    (value ?? '').replace(/\D/g, '').slice(0, 11);

  const cpfQuality = (value?: string) => normalizeCpfDigits(value).length;

  const getMemberId = (member: GroupLifeMemberCrudResponse): string =>
    String(member.id ?? member.policyElementId ?? '');

  const getMemberKey = (member: GroupLifeMemberCrudResponse): string =>
    `${normalizeMemberText(member.name)}::${member.dateOfBirth ?? ''}::${normalizeMemberText(member.insurableGroup)}`;

  const editable = showAddMember && !!policyId;

  const asyncImportQuery = useAsyncMembersImport({
    policyId: policyId ?? null,
    importId: asyncImport?.importId ?? null,
    enabled: !!asyncImport?.importId,
    isProposal: true,
  });

  // Também invalida as páginas por target (`/members/page`) das telas de
  // análise, que não leem a lista completa da proposta.
  const invalidateLives = () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: groupLifeProposalLivesKey(policyId),
      }),
      queryClient.invalidateQueries({
        queryKey: ['group-life', 'members-page', policyId],
      }),
    ]);

  /**
   * Recarrega a lista UMA vez. Antes havia um segundo refetch agendado 1,2s
   * depois "por garantia": duas varreduras completas de vidas (membros por
   * target + tarifação) a cada importação, dobrando a espera do corretor sem
   * trazer dado novo — a invalidação já busca o estado atual.
   */
  const refreshLives = async () => {
    await invalidateLives();
    await queryClient.refetchQueries({
      queryKey: groupLifeProposalLivesKey(policyId),
    });
  };


  /**
   * A edição da vida mexeu em algum campo que entra na tarifa? Nome e CPF são
   * cadastro puro — o prêmio não muda e o recálculo é dispensado. Nascimento,
   * sexo, grupo segurável, capital, afastamento e CID mudam a tarifa.
   */
  const changesRatingFields = (
    previous: PmeLifeRow | null,
    next: MemberData
  ): boolean => {
    if (!previous) return true;
    const normalizeCids = (cids?: string[]) =>
      (cids ?? [])
        .map(cid => cid.trim().toUpperCase())
        .filter(Boolean)
        .sort()
        .join('|');
    const previousBirthDate = previous.birthDate === '—' ? '' : previous.birthDate;
    const previousGroup =
      previous.insurableGroup === '—' ? '' : previous.insurableGroup;
    const previousCapital = previous.capital === '—' ? '' : previous.capital;
    return (
      previousBirthDate !== next.dateOfBirth ||
      (previous.gender || '') !== next.gender ||
      normalizeMemberText(previousGroup) !==
        normalizeMemberText(next.insurableGroup) ||
      normalizeMemberText(previousCapital) !== normalizeMemberText(next.capital) ||
      !!previous.awayFromWork !== !!next.awayFromWork ||
      normalizeCids(previous.cids) !== normalizeCids(next.cids)
    );
  };

  const mapMemberToLifeRow = (
    member: GroupLifeMemberCrudResponse,
    lifeTargetId = targetId,
    fallback?: Partial<PmeLifeRow>,
  ): PmeLifeRow => ({
    id:
      String(member.id ?? member.policyElementId ?? fallback?.id ?? crypto.randomUUID()),
    status: (member.awayFromWork ? 'inactive' : 'active') as PmeLifeRow['status'],
    insurableGroup: member.insurableGroup || fallback?.insurableGroup || '—',
    capital: member.capital || fallback?.capital || '—',
    multiplySalary:
      plan?.modality === 'MULTIPLE_SALARY' &&
      (!!member.capital?.trim() || !!fallback?.multiplySalary),
    sex: member.gender || fallback?.sex || '—',
    birthDate: member.dateOfBirth || fallback?.birthDate || '—',
    name: member.name || fallback?.name || '—',
    memberId: String(member.id ?? member.policyElementId ?? fallback?.memberId ?? ''),
    targetId: lifeTargetId,
    cpf: member.cpf || '',
    gender: member.gender || fallback?.gender || '',
    cids: member.cids ?? fallback?.cids ?? [],
    awayFromWork: member.awayFromWork ?? fallback?.awayFromWork ?? false,
  });

  const upsertLifeInCache = (
    member: GroupLifeMemberCrudResponse,
    options?: {
      targetId?: string;
      prepend?: boolean;
      previousLife?: Partial<PmeLifeRow> | null;
    },
  ) => {
    const lifeTargetId = options?.targetId ?? targetId;
    queryClient.setQueriesData<PmeLifeRow[]>(
      { queryKey: groupLifeProposalLivesKey(policyId) },
      currentLives => {
        const nextLife = mapMemberToLifeRow(member, lifeTargetId, options?.previousLife ?? undefined);
        const livesList = currentLives ?? [];
        const matchIndex = livesList.findIndex(life => {
          if (nextLife.memberId && life.memberId === nextLife.memberId) return true;
          return !!member.policyElementId && life.memberId === String(member.policyElementId);
        });

        if (matchIndex === -1) {
          return options?.prepend ? [nextLife, ...livesList] : [...livesList, nextLife];
        }

        return livesList.map((life, index) => (index === matchIndex ? nextLife : life));
      },
    );
  };

  const removeLifeFromCache = (memberId?: string) => {
    if (!memberId) return;
    queryClient.setQueriesData<PmeLifeRow[]>(
      { queryKey: groupLifeProposalLivesKey(policyId) },
      currentLives => (currentLives ?? []).filter(life => life.memberId !== memberId),
    );
  };

  useEffect(() => {
    if (!detailsLife) return;

    const currentById = detailsLife.memberId
      ? lives.find(life => life.memberId === detailsLife.memberId)
      : undefined;

    const currentByIdentity = lives.find(
      life =>
        normalizeMemberText(life.name) === normalizeMemberText(detailsLife.name) &&
        life.birthDate === detailsLife.birthDate &&
        normalizeMemberText(life.insurableGroup) ===
          normalizeMemberText(detailsLife.insurableGroup)
    );

    const next = currentById ?? currentByIdentity;
    if (next) {
      setDetailsLife(previous => {
        if (!previous) return previous;
        return previous === next ? previous : next;
      });
      return;
    }

    setDetailsLife(null);
  }, [detailsLife, lives]);

  const pickKeeper = (
    members: GroupLifeMemberCrudResponse[],
    preferredIds: Set<string>
  ): GroupLifeMemberCrudResponse => {
    const preferred = members.find(member => preferredIds.has(getMemberId(member)));
    if (preferred) return preferred;
    return [...members].sort((a, b) => cpfQuality(b.cpf) - cpfQuality(a.cpf))[0];
  };

  /**
   * Remove as vidas duplicadas que a importação pode ter deixado (mesma
   * pessoa em dois registros) e promove o melhor CPF para a vida que fica.
   *
   * Tudo em PARALELO com concorrência limitada: a versão anterior fazia um
   * `await` por exclusão dentro do laço dos grupos — numa planilha grande, uma
   * requisição por vez, e a tela ficava minutos no overlay de importação.
   * A ordem que importa é preservada: primeiro TODAS as exclusões, depois as
   * atualizações de CPF (o InsureMO recusa o update enquanto o CPF ainda está
   * ocupado pela duplicada).
   */
  const reconcileImportedDuplicates = async (existingMemberIds: string[]) => {
    if (!policyId) return { updated: 0, removed: 0 };

    const membersResponse = await groupLifeService.listMembersByTarget({
      quoteId: policyId,
      targetId,
    });

    if (!membersResponse.success || !membersResponse.data) {
      return { updated: 0, removed: 0 };
    }

    const preferredIds = new Set(existingMemberIds);
    const groupBy = (
      keyOf: (member: GroupLifeMemberCrudResponse) => string | null
    ): GroupLifeMemberCrudResponse[][] => {
      const groups = new Map<string, GroupLifeMemberCrudResponse[]>();
      for (const member of membersResponse.data ?? []) {
        const key = keyOf(member);
        if (!key) continue;
        const list = groups.get(key) ?? [];
        list.push(member);
        groups.set(key, list);
      }
      return [...groups.values()].filter(members => members.length > 1);
    };

    // Duplicadas por identidade (nome + nascimento + grupo) e por CPF.
    const identityGroups = groupBy(member => {
      const key = getMemberKey(member);
      return !key || key === '::' ? null : key;
    });
    const cpfGroups = groupBy(member => {
      const cpf = normalizeCpfDigits(member.cpf);
      return cpf.length === 11 ? cpf : null;
    });

    const idsToDelete = new Set<string>();
    const cpfUpdates: { keeper: GroupLifeMemberCrudResponse; cpf: string }[] = [];

    for (const members of [...identityGroups, ...cpfGroups]) {
      const keeper = pickKeeper(members, preferredIds);
      const keeperId = getMemberId(keeper);
      if (!keeperId) continue;
      for (const member of members) {
        const memberId = getMemberId(member);
        if (memberId && memberId !== keeperId) idsToDelete.add(memberId);
      }
    }

    // O melhor CPF do grupo de identidade migra para a vida que fica.
    for (const members of identityGroups) {
      const keeper = pickKeeper(members, preferredIds);
      const keeperId = getMemberId(keeper);
      if (!keeperId || idsToDelete.has(keeperId)) continue;
      const bestCpfMember = [...members].sort(
        (a, b) => cpfQuality(b.cpf) - cpfQuality(a.cpf)
      )[0];
      const bestCpf = normalizeCpfDigits(bestCpfMember.cpf);
      if (bestCpf.length === 11 && normalizeCpfDigits(keeper.cpf) !== bestCpf) {
        cpfUpdates.push({ keeper, cpf: bestCpfMember.cpf });
      }
    }

    if (idsToDelete.size === 0 && cpfUpdates.length === 0) {
      return { updated: 0, removed: 0 };
    }

    const deletions = await mapWithConcurrency(
      [...idsToDelete],
      RECONCILE_CONCURRENCY,
      memberId => groupLifeService.deleteMember({ quoteId: policyId, memberId })
    );
    const removed = deletions.filter(
      result => result.status === 'fulfilled' && result.value.success
    ).length;

    const updates = await mapWithConcurrency(
      cpfUpdates,
      RECONCILE_CONCURRENCY,
      ({ keeper, cpf }) =>
        groupLifeService.updateMember({
          quoteId: policyId,
          memberId: getMemberId(keeper),
          input: {
            targetId,
            UniqueID: crypto.randomUUID(),
            cpf,
            name: keeper.name,
            dateOfBirth: keeper.dateOfBirth,
            gender: keeper.gender,
            insurableGroup: keeper.insurableGroup,
            capital: keeper.capital,
            awayFromWork: keeper.awayFromWork,
            cids: keeper.cids,
          },
        })
    );
    const updated = updates.filter(
      result => result.status === 'fulfilled' && result.value.success
    ).length;

    return { updated, removed };
  };

  useEffect(() => {
    if (!asyncImport?.importId) {
      completedImportIdRef.current = null;
      return;
    }

    const finalImportStatus = asyncImportQuery.data?.status;
    if (
      asyncImportQuery.status === 'COMPLETED' &&
      (finalImportStatus === 'COMPLETED' || finalImportStatus === 'UPDATE')
    ) {
      if (completedImportIdRef.current === asyncImport.importId) return;
      completedImportIdRef.current = asyncImport.importId;

      // Reconcilia, recarrega a lista e libera a tela — sem espera artificial.
      // Antes havia um `setTimeout` de 4s antes de fechar o overlay e disparar
      // o recálculo, somando ao tempo já gasto na importação.
      void (async () => {
        const result = await reconcileImportedDuplicates(
          asyncImport.existingMemberIds
        );
        await refreshLives();
        setAsyncImport(null);
        if (result.updated > 0 || result.removed > 0) {
          toaster.create({
            type: 'success',
            title: 'Vidas reconciliadas',
            description: `${result.updated} vida(s) atualizada(s) e ${result.removed} duplicada(s) removida(s).`,
          });
        }
        // `UPDATE` = o backend comparou a planilha com as vidas atuais e só
        // achou diferença de nome/CPF: a tarifa não muda, então não recalcula.
        // A reconciliação que removeu duplicada mexeu na quantidade de vidas,
        // e aí o recálculo volta a ser obrigatório.
        onImportCompleted?.({
          requiresRecalculation:
            finalImportStatus !== 'UPDATE' || result.removed > 0,
        });
      })();
      return;
    }

    if (asyncImportQuery.status === 'FAILED') {
      void refreshLives();
    }
  }, [
    asyncImport,
    asyncImportQuery.data?.status,
    asyncImportQuery.status,
    onImportCompleted,
    policyId,
  ]);

  const columns = useMemo(
    () =>
      createLivesColumns({
        editable,
        showIncompleteStatus,
        plan,
        multiplySalary,
        onViewDetails: setDetailsLife,
        onEdit: setEditLife,
        onDelete: setDeleteLife,
      }),
    [editable, multiplySalary, plan, showIncompleteStatus]
  );
  const detailsDisplayLife = useMemo(() => {
    const shouldMultiply = multiplySalary || !!detailsLife?.multiplySalary;
    if (!detailsLife || !shouldMultiply) return detailsLife;
    const capital = resolveMemberDisplayCapital(detailsLife.capital, plan, {
      multiplySalary: true,
    });
    return { ...detailsLife, capital: capital || detailsLife.capital };
  }, [detailsLife, multiplySalary, plan]);
  const serverPaginated = !!onPageChange;
  const pageSize = configuredPageSize ?? (lives.length || 6);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const paginatedLives = useMemo(() => {
    if (serverPaginated) return lives;
    const start = (currentPage - 1) * pageSize;
    return lives.slice(start, start + pageSize);
  }, [currentPage, lives, pageSize, serverPaginated]);

  useEffect(() => {
    setCurrentPage(page => Math.min(page, totalPages));
  }, [totalPages]);

  const addMember = useMutation({
    mutationFn: async (data: MemberData) => {
      if (!policyId) throw new Error('PolicyId não encontrado');
      const uniqueId = crypto.randomUUID();
      const input: GroupLifeMemberCrudInput = {
        targetId,
        UniqueID: uniqueId,
        ...data,
      };
      const res = await groupLifeService.addMember({ quoteId: policyId, input });
      if (!res.success) throw new Error(res.error ?? 'Falha ao adicionar a vida');
      return { member: res.data, uniqueId };
    },
    onSuccess: (response, payload) => {
      const member = response.member;
      if (member) {
        const addedMemberId = getMemberId(member);
        if (addedMemberId) {
          memberUniqueIdsRef.current.set(addedMemberId, response.uniqueId);
        }
        upsertLifeInCache(member, {
          targetId,
          prepend: true,
          previousLife: {
            targetId,
            cpf: payload.cpf,
            name: payload.name,
            birthDate: payload.dateOfBirth,
            sex: payload.gender,
            gender: payload.gender,
            insurableGroup: payload.insurableGroup,
            capital: payload.capital,
            awayFromWork: payload.awayFromWork,
            cids: payload.cids,
          },
        });
      }
      void invalidateLives();
      setAddOpen(false);
      toaster.create({ type: 'success', title: 'Vida adicionada' });
      onLivesChange?.({ requiresRecalculation: true });
    },
    onError: err =>
      toaster.create({
        type: 'error',
        title: 'Não foi possível adicionar a vida',
        description: err instanceof Error ? err.message : 'Tente novamente.',
      }),
  });

  const updateMember = useMutation({
    mutationFn: async (data: MemberData) => {
      if (!policyId) throw new Error('PolicyId não encontrado');
      const memberId = editLife?.memberId;
      if (!memberId) throw new Error('Vida sem identificador para edição');
      const uniqueId =
        memberUniqueIdsRef.current.get(memberId) ?? crypto.randomUUID();
      memberUniqueIdsRef.current.set(memberId, uniqueId);
      const input: GroupLifeMemberCrudInput = {
        targetId: editLife?.targetId,
        UniqueID: uniqueId,
        ...data,
      };
      const res = await groupLifeService.updateMember({
        quoteId: policyId,
        memberId,
        input,
      });
      if (!res.success) throw new Error(res.error ?? 'Falha ao editar a vida');
      return { member: res.data, uniqueId };
    },
    onSuccess: (response, payload) => {
      const member = response.member;
      if (member) {
        const updatedMemberId = getMemberId(member);
        if (updatedMemberId) {
          memberUniqueIdsRef.current.set(updatedMemberId, response.uniqueId);
        }
        upsertLifeInCache(member, {
          targetId: editLife?.targetId,
          previousLife: editLife
            ? {
                ...editLife,
                cpf: payload.cpf,
                name: payload.name,
                birthDate: payload.dateOfBirth,
                sex: payload.gender,
                gender: payload.gender,
                insurableGroup: payload.insurableGroup,
                capital: payload.capital,
                awayFromWork: payload.awayFromWork,
                cids: payload.cids,
              }
            : undefined,
        });
      }
      void invalidateLives();
      setEditLife(null);
      toaster.create({ type: 'success', title: 'Vida atualizada' });
      // Mesma regra da planilha: editar só nome e/ou CPF não muda a tarifa.
      onLivesChange?.({
        requiresRecalculation: changesRatingFields(editLife, payload),
      });
    },
    onError: err =>
      toaster.create({
        type: 'error',
        title: 'Não foi possível editar a vida',
        description: err instanceof Error ? err.message : 'Tente novamente.',
      }),
  });

  const deleteMember = useMutation({
    mutationFn: async (life: PmeLifeRow) => {
      if (!policyId) throw new Error('PolicyId não encontrado');
      if (!life.memberId) throw new Error('Vida sem identificador para exclusão');
      const res = await groupLifeService.deleteMember({
        quoteId: policyId,
        memberId: life.memberId,
      });
      if (!res.success) throw new Error(res.error ?? 'Falha ao excluir a vida');
      return res.data;
    },
    onSuccess: (_data, life) => {
      removeLifeFromCache(life.memberId);
      if (detailsLife?.memberId === life.memberId) {
        setDetailsLife(null);
      }
      void invalidateLives();
      setDeleteLife(null);
      toaster.create({ type: 'success', title: 'Vida excluída' });
      onLivesChange?.({ requiresRecalculation: true });
    },
    onError: err =>
      toaster.create({
        type: 'error',
        title: 'Não foi possível excluir a vida',
        description: err instanceof Error ? err.message : 'Tente novamente.',
      }),
  });

  const handleExport = async () => {
    if (!policyId || isExporting) return;
    const targets: LivesExportTarget[] = exportTargets?.length
      ? exportTargets
      : [{ targetId }];
    setIsExporting(true);
    try {
      for (const target of targets) {
        const res = await groupLifeService.downloadMembersExport({
          quoteId: policyId,
          targetId: target.targetId,
          filename: target.label,
        });
        if (!res.success) {
          toaster.create({
            type: 'error',
            title: 'Exportar vidas',
            description: res.error ?? 'Não foi possível exportar as vidas.',
          });
          return;
        }
      }
      toaster.create({
        type: 'success',
        title: 'Exportação concluída',
        description:
          targets.length > 1
            ? `${targets.length} planilhas de vidas foram geradas com sucesso.`
            : 'A planilha de vidas foi gerada com sucesso.',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportConfirm = async (file: File) => {
    if (!policyId || isImporting) return;
    const existingMemberIds = lives
      .map(life => life.memberId)
      .filter((value): value is string => !!value);
    setIsImporting(true);
    setImportOpen(false);
    setAsyncImport({
      importId: '',
      targetId,
      totalExpected: 0,
      existingMemberIds,
    });
    try {
      const result = await groupLifeService.uploadMembersSheetAsync({
        quoteId: policyId,
        targetId,
        file,
        isProposal: true,
        plan: {
          modality: plan?.modality,
          uniformCapital: plan?.uniformCapital,
          numberOfSalaries: plan?.numberOfSalaries,
          minimumIndividualCapital: plan?.minimumIndividualCapital,
          maximumIndividualCapital: plan?.maximumIndividualCapital,
        },
      });

      if (!result.success || !result.data) {
        setAsyncImport(null);
        if (result.duplicates) {
          setDuplicateCpfs(result.duplicates);
          return;
        }
        toaster.create({
          type: 'error',
          title: 'Importação de vidas',
          description:
            result.error ?? 'Não foi possível processar a planilha.',
        });
        return;
      }

      const { parseErrors, totalExpected, members } = result.data;
      const describe = (error: {
        rowNumber?: number;
        column?: string;
        message?: string;
      }) =>
        `Linha ${error.rowNumber}${error.column ? ` (${error.column})` : ''}: ${error.message}`;

      const missingCpfErrors = members
        .filter(member => !member.cpf?.trim())
        .map(member => `Linha ${member.rowNumber} (CPF): CPF é obrigatório.`);
      const cpfParseErrors = parseErrors
        .filter(error =>
          /cpf/i.test(`${error.column ?? ''} ${error.message ?? ''}`)
        )
        .map(describe);
      // Nome é obrigatório na proposta — o backend também barra o upload, mas
      // apontar a linha aqui poupa uma ida ao servidor.
      const missingNameErrors = requireName
        ? members
            .filter(member => !member.name?.trim())
            .map(member => `Linha ${member.rowNumber} (NOME): Nome é obrigatório.`)
        : [];
      const nameParseErrors = requireName
        ? parseErrors
            .filter(error =>
              /nome/i.test(`${error.column ?? ''} ${error.message ?? ''}`)
            )
            .map(describe)
        : [];

      const blockingErrors = [
        ...cpfParseErrors,
        ...missingCpfErrors,
        ...nameParseErrors,
        ...missingNameErrors,
      ];
      if (blockingErrors.length > 0) {
        setAsyncImport(null);
        setImportErrors(
          blockingErrors.filter(
            (error, index, list) => list.indexOf(error) === index
          )
        );
        const missingName =
          nameParseErrors.length > 0 || missingNameErrors.length > 0;
        const missingCpf =
          cpfParseErrors.length > 0 || missingCpfErrors.length > 0;
        toaster.create({
          type: 'error',
          title: 'Importação bloqueada',
          description: `A planilha contém vida(s) sem ${
            missingCpf && missingName
              ? 'CPF ou sem nome'
              : missingName
                ? 'nome'
                : 'CPF'
          }. Corrija os dados e tente novamente.`,
        });
        return;
      }

      setAsyncImport({
        importId: result.data.importId,
        targetId,
        totalExpected,
        existingMemberIds,
        status: result.data.status,
      });
    } catch (error) {
      setAsyncImport(null);
      toaster.create({
        type: 'error',
        title: 'Importação de vidas',
        description:
          error instanceof Error
            ? error.message
            : 'Não foi possível processar a planilha.',
      });
    } finally {
      setIsImporting(false);
    }
  };

  const editInitialMember: MemberData | undefined = editLife
    ? {
        cpf: editLife.cpf ?? '',
        name: editLife.name === '—' ? '' : editLife.name,
        dateOfBirth: editLife.birthDate === '—' ? '' : editLife.birthDate,
        gender: (editLife.gender || 'M') as 'M' | 'F',
        insurableGroup:
          editLife.insurableGroup === '—' ? '' : editLife.insurableGroup,
        capital: editLife.capital === '—' ? '' : editLife.capital,
        awayFromWork: !!editLife.awayFromWork,
        cids: editLife.cids ?? [],
      }
    : undefined;

  const isCpfRegistered = (cpf: string, excludedMemberId?: string) =>
    isCpfAlreadyRegistered(
      cpf,
      (cpfLookupLives ?? lives)
        .filter(life => life.memberId !== excludedMemberId)
        .map(life => life.cpf)
    );

  return (
    <div className='flex flex-col gap-4'>
      {asyncImport && (
        <AsyncImportOverlay
          status={asyncImportQuery.status}
          progress={asyncImportQuery.progress}
          errors={asyncImportQuery.errors}
          onDismiss={() => setAsyncImport(null)}
        />
      )}

      {showTitle && (
        <Text className='font-montserrat text-base font-normal text-alba-black-500'>
          Lista de Vidas
        </Text>
      )}
      <ul
        aria-label='Legenda dos status das vidas'
        className='flex list-none flex-wrap gap-x-6 gap-y-2 text-sm text-alba-black-500'
      >
        <li className='flex items-center gap-2'>
          <span
            aria-hidden='true'
            className='h-2.5 w-2.5 shrink-0 rounded-full bg-alba-success-green-500'
          />
          Verde:{' '}
          {showIncompleteStatus ? 'Dados completos.' : 'Sem afastamento.'}
        </li>
        {showIncompleteStatus && (
          <li className='flex items-center gap-2'>
            <span
              aria-hidden='true'
              className='h-2.5 w-2.5 shrink-0 rounded-full bg-yellow-500'
            />
            Amarelo: Informações pendentes.
          </li>
        )}
        <li className='flex items-center gap-2'>
          <span
            aria-hidden='true'
            className='h-2.5 w-2.5 shrink-0 rounded-full bg-alba-gray-400'
          />
          Cinza: Afastamento.
        </li>
      </ul>

      <div className='flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between'>
        <div className='flex flex-1 items-center gap-4'>
          <div className='flex w-44 items-center gap-2 rounded-lg border border-alba-gray-300 px-3 py-2'>
            <Text className='text-sm text-alba-gray-600'>Todos</Text>
          </div>
          <div className='flex flex-1 items-center gap-2 rounded-lg border border-alba-gray-300 px-3 py-2'>
            <Search className='h-4 w-4 text-alba-gray-600' />
            <input
              placeholder='Buscar'
              className='w-full text-sm text-alba-black-500 outline-none'
            />
          </div>
        </div>
        <div className='flex items-center gap-3'>
          <Button
            variant='secondary'
            size='md'
            className='flex items-center gap-2'
            disabled={!policyId || isExporting}
            loading={isExporting}
            onClick={() => void handleExport()}
          >
            <Download className='h-4 w-4' />
            Exportar
          </Button>
          {showImport && (
            <Button
              variant='secondary'
              size='md'
              className='flex items-center gap-2'
              disabled={!policyId || isImporting}
              onClick={() => setImportOpen(true)}
            >
              <Upload className='h-4 w-4' />
              Importar
            </Button>
          )}
          {showAddMember && (
            <Button
              variant='primary'
              size='md'
              className='flex items-center gap-2'
              onClick={() => setAddOpen(true)}
              disabled={!policyId}
            >
              <UserPlus className='h-4 w-4' />
              Adicionar vidas
            </Button>
          )}
        </div>
      </div>

      <DataTable
        columns={columns}
        data={paginatedLives}
        pageSize={pageSize}
        totalItems={total}
        totalPages={totalPages}
        pageSizeOptions={
          pageSizeOptions ??
          (configuredPageSize ? [configuredPageSize] : undefined)
        }
        loading={isLoading}
        onPageChange={(page, size) => {
          if (size != null && size !== pageSize) {
            setCurrentPage(1);
            onPageChange?.(1, size);
            return;
          }
          setCurrentPage(page);
          onPageChange?.(page, size);
        }}
      />

      <LifeDetailsModal
        isOpen={!!detailsLife}
        onClose={() => setDetailsLife(null)}
        life={detailsDisplayLife}
      />

      {showAddMember && (
        <AddMemberModal
          isOpen={addOpen}
          onClose={() => setAddOpen(false)}
          onConfirm={data => addMember.mutate(data)}
          isSubmitting={addMember.isPending}
          modality={plan?.modality}
          uniformCapital={plan?.uniformCapital}
          minIndividualCapital={plan?.minimumIndividualCapital}
          maxIndividualCapital={plan?.maximumIndividualCapital}
          numberOfSalaries={plan?.numberOfSalaries}
          isCpfRegistered={cpf => isCpfRegistered(cpf)}
          requireCpf
          requireName={requireName}
        />
      )}

      {editable && (
        <AddMemberModal
          isOpen={!!editLife}
          onClose={() => setEditLife(null)}
          onConfirm={data => updateMember.mutate(data)}
          isSubmitting={updateMember.isPending}
          initialMember={editInitialMember}
          disableBirthDateEdit
          modality={plan?.modality}
          uniformCapital={plan?.uniformCapital}
          minIndividualCapital={plan?.minimumIndividualCapital}
          maxIndividualCapital={plan?.maximumIndividualCapital}
          numberOfSalaries={plan?.numberOfSalaries}
          isCpfRegistered={cpf => isCpfRegistered(cpf, editLife?.memberId)}
          requireCpf
          requireName={requireName}
        />
      )}

      <ImportMembersModal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onConfirm={handleImportConfirm}
      />

      <DuplicateMembersDialog
        isOpen={duplicateCpfs !== null}
        onClose={() => setDuplicateCpfs(null)}
        payload={duplicateCpfs}
      />

      <Dialog.Root
        open={importErrors.length > 0}
        onOpenChange={(details: { open: boolean }) => {
          if (!details.open) setImportErrors([]);
        }}
      >
        <Dialog.Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content className='w-full max-w-xl'>
              <Dialog.Header>
                <Dialog.Title>Importação bloqueada</Dialog.Title>
                <Dialog.CloseTrigger asChild>
                  <Dialog.CloseButton />
                </Dialog.CloseTrigger>
              </Dialog.Header>
              <Dialog.Body>
                <Text>
                  Corrija os dados abaixo, incluindo o CPF quando a vida já
                  estiver cadastrada, e envie a planilha novamente.
                </Text>
                <ul className='mt-3 max-h-64 list-disc space-y-1 overflow-y-auto pl-5'>
                  {importErrors.map(error => (
                    <li key={error}>
                      <Text>{error}</Text>
                    </li>
                  ))}
                </ul>
              </Dialog.Body>
              <Dialog.Footer>
                <Button
                  variant='primary'
                  size='md'
                  onClick={() => setImportErrors([])}
                >
                  Entendi
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Dialog.Portal>
      </Dialog.Root>

      <Dialog.Root
        open={!!deleteLife}
        onOpenChange={(details: { open: boolean }) => {
          if (!details.open) setDeleteLife(null);
        }}
      >
        <Dialog.Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content className='w-full max-w-md'>
              <Dialog.Header>
                <Dialog.Title>
                  <div className='flex items-center gap-2'>
                    <AlertTriangle className='h-5 w-5 text-alba-error-red-500' />
                    Excluir vida
                  </div>
                </Dialog.Title>
                <Dialog.CloseTrigger asChild>
                  <Dialog.CloseButton />
                </Dialog.CloseTrigger>
              </Dialog.Header>
              <Dialog.Body>
                <Text>
                  Tem certeza que deseja excluir a vida
                  {deleteLife?.name ? ` "${deleteLife.name}"` : ''}? Esta ação
                  não poderá ser desfeita.
                </Text>
              </Dialog.Body>
              <Dialog.Footer>
                <Button
                  variant='ghost'
                  size='md'
                  disabled={deleteMember.isPending}
                  onClick={() => setDeleteLife(null)}
                >
                  Cancelar
                </Button>
                <Button
                  variant='primary'
                  size='md'
                  disabled={deleteMember.isPending}
                  loading={deleteMember.isPending}
                  onClick={() => deleteLife && deleteMember.mutate(deleteLife)}
                >
                  Excluir
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
